import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { anonymousSdk, refreshSession, sdk, setSessionExpiredHandler } from './graphql';
import { tokenStore } from './token-store';
import type { UserBriefFragment, UserRole } from '@/graphql/generated';

export type SessionUser = UserBriefFragment;

interface AuthContextValue {
  user: SessionUser | null;
  /** `true` mientras se intenta restaurar la sesión desde el refresh token. */
  isBootstrapping: boolean;
  /** El servidor exige cambiar la contraseña antes de dejar entrar. */
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** `true` si el rol del usuario está en la lista. */
  can: (roles: readonly UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isBootstrapping, setBootstrapping] = useState(true);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setMustChangePassword(false);
    queryClient.clear();
  }, [queryClient]);

  // El cliente GraphQL avisa cuando el refresco fracasa y ya no hay sesión que
  // salvar; aquí se traduce en «vuelve a la pantalla de acceso».
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      setMustChangePassword(false);
      queryClient.clear();
    });
    return () => setSessionExpiredHandler(null);
  }, [queryClient]);

  // Al abrir la app se intenta canjear el refresh token guardado. Si funciona,
  // la persona no ve la pantalla de acceso; si no, se limpia sin ruido.
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!tokenStore.getRefreshToken()) {
        if (!cancelled) setBootstrapping(false);
        return;
      }
      // `refreshSession` comparte la petición en vuelo. Importa: el token de
      // refresco es de un solo uso y en desarrollo StrictMode monta este
      // efecto dos veces; sin compartirla, el segundo canje llegaría con un
      // token ya gastado y cerraría la sesión recién abierta.
      const payload = await refreshSession();
      if (cancelled) return;
      if (payload) {
        setUser(payload.user);
        setMustChangePassword(payload.mustChangePassword);
      }
      setBootstrapping(false);
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const { login: payload } = await anonymousSdk.Login({
        email,
        password,
        device: navigator.userAgent.slice(0, 120),
      });
      tokenStore.setSession(payload.accessToken, payload.refreshToken, payload.expiresIn);
      setUser(payload.user);
      setMustChangePassword(payload.mustChangePassword);
    },
    [],
  );

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.getRefreshToken();
    try {
      // Invalidar el refresh token en el servidor es lo que hace que cerrar
      // sesión signifique algo; si falla, se cierra igualmente en el cliente.
      await anonymousSdk.Logout({ refreshToken });
    } catch {
      // Sin conexión no se puede avisar al servidor; se sigue adelante.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await sdk.ChangePassword({ currentPassword, newPassword });
    setMustChangePassword(false);
  }, []);

  const can = useCallback(
    (roles: readonly UserRole[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, isBootstrapping, mustChangePassword, login, logout, changePassword, can }),
    [user, isBootstrapping, mustChangePassword, login, logout, changePassword, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}

/** Roles que pueden entrar a Configuración y a los ajustes de negocio. */
export const ADMIN_ROLES: readonly UserRole[] = ['ADMIN', 'SUPERVISOR'];
/** Roles que pueden escribir (el rol LECTURA sólo consulta). */
export const WRITE_ROLES: readonly UserRole[] = ['ADMIN', 'SUPERVISOR', 'ASESOR', 'TECNICO'];
